import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { useUser } from '../../hooks/UserContext';

const AdminDashboard = () => {
    const { systemAdminRole } = useUser();
    const isAdminRole = systemAdminRole === 'admin';

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/v1/admin/dashboard');
            if (!response.ok) throw new Error('Failed to fetch dashboard data');
            const result = await response.json();
            setData(result);
        } catch (err) {
            console.error(err);
            setError('データの取得に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const handleRefresh = async () => {
        if (!window.confirm('現在のデータでスナップショットを再作成しますか？（数秒かかります）')) return;
        setRefreshing(true);
        try {
            const response = await apiClient.post('/v1/admin/dashboard/refresh');
            if (!response.ok) throw new Error('Failed to refresh data');
            await fetchDashboardData();
            alert('データを更新しました。');
        } catch (err) {
            console.error(err);
            alert('データの更新に失敗しました。');
        } finally {
            setRefreshing(false);
        }
    };

    const getOperationTypeName = (type) => {
        const types = {
            tenant_suspend: '利用停止',
            tenant_resume: '利用再開',
            invoice_lock: '請求書発行停止',
            invoice_unlock: '請求書発行再開',
            user_disable: 'アカウント無効化',
            user_enable: 'アカウント有効化',
            force_logout: '強制ログアウト',
            invoice_resend: '請求書再送信',
            trial_start: 'トライアル開始',
            trial_extend: 'トライアル延長',
            trial_end_schedule: 'トライアル終了予約',
            stripe_settings_updated: 'Stripe共通設定変更',
            maintenance_enabled: 'メンテナンスモード有効化',
            maintenance_disabled: 'メンテナンスモード無効化',
            dashboard_refresh: 'ダッシュボード統計更新',
            trial_settings_updated: 'トライアル受付設定変更',
            refund_execute: '個別返金実行',
            credit_execute: '個別返金予約',
            system_user_create: 'システムユーザー追加',
            system_user_update: 'システムユーザー編集',
            system_user_disable: 'システムユーザー無効化',
            system_user_enable: 'システムユーザー再有効化',
            bulk_refund_create: '一括返金作成',
            bulk_refund_execute: '一括返金実行'
        };
        return types[type] || type;
    };

    const getTargetTypeName = (type) => {
        const types = {
            tenant: '企業',
            user: 'ユーザー',
            invoice: '請求書',
            system_settings: 'システム設定',
            dashboard: 'ダッシュボード',
            system_user: 'システムユーザー',
            bulk_refund: '一括返金'
        };
        return types[type] || type;
    };

    const getJobTypeName = (type) => {
        const types = {
            'invoice-upload': '請求書発行',
            'customer-upload': '顧客登録',
            'customer-delete': '顧客削除',
            'invoice_import': '請求書発行',
            'customer_import': '顧客登録'
        };
        return types[type] || type || '-';
    };

    if (loading) return <div className="p-8 text-center">読み込み中...</div>;
    if (error) return <div className="p-8 text-center text-red-600">{error}</div>;

    const { snapshot, csvErrors, stripeErrors, adminOperations, monitoring } = data;

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">システム管理者ダッシュボード</h2>
                <button 
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className={`bg-indigo-600 text-white px-4 py-2 rounded-md font-bold shadow hover:bg-indigo-700 focus:outline-none disabled:bg-indigo-300`}
                >
                    {refreshing ? '更新中...' : 'データを今すぐ更新'}
                </button>
            </div>

            {/* システム監視状態 */}
            <section className="mb-10">
                <h3 className="text-xl font-bold mb-4 border-l-4 border-blue-600 pl-2">システム監視状態 (Cloudflare連携)</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white p-6 rounded-lg shadow">
                        <p className="text-sm text-gray-500 mb-1">現在の状態</p>
                        <p className={`text-2xl font-bold ${
                            monitoring.status === 'normal' ? 'text-green-600' : 
                            monitoring.status === 'maintenance' ? 'text-blue-600' : 'text-red-600'
                        }`}>
                            {monitoring.status === 'normal' ? '正常' : 
                             monitoring.status === 'maintenance' ? 'メンテナンス中' : '異常検知'}
                        </p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow">
                        <p className="text-xs text-gray-500 mb-1">最終正常日時</p>
                        <p className="text-sm font-bold">{monitoring.stats.lastOkAt ? new Date(monitoring.stats.lastOkAt).toLocaleString('ja-JP') : '-'}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
                        <p className="text-xs text-gray-500 mb-1">最終障害日時</p>
                        <p className="text-sm font-bold">{monitoring.stats.lastFailedAt ? new Date(monitoring.stats.lastFailedAt).toLocaleString('ja-JP') : '-'}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                        <p className="text-xs text-gray-500 mb-1">最終復旧日時</p>
                        <p className="text-sm font-bold">{monitoring.stats.lastRecoveryAt ? new Date(monitoring.stats.lastRecoveryAt).toLocaleString('ja-JP') : '-'}</p>
                    </div>
                </div>
                <div className="mt-2 text-right">
                    <Link to="/admin/failure-history" className="text-sm text-indigo-600 hover:underline">障害履歴一覧へ</Link>
                </div>
            </section>

            {!snapshot && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
                    <p className="text-yellow-700">
                        集計データ（スナップショット）がまだ作成されていません。「データを今すぐ更新」ボタンを押すか、翌朝8:00の自動更新をお待ちください。
                    </p>
                </div>
            )}

            {snapshot && (
                <>
                    {/* 企業状況（アドミンのみ表示） */}
                    {isAdminRole && (
                        <section className="mb-10">
                            <h3 className="text-xl font-bold mb-4 border-l-4 border-indigo-600 pl-2">企業状況 ({snapshot.snapshot_date})</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-white p-6 rounded-lg shadow">
                                    <p className="text-sm text-gray-500 mb-1">総企業数</p>
                                    <p className="text-3xl font-bold">{snapshot.company.total}</p>
                                </div>
                                <div className="bg-white p-6 rounded-lg shadow">
                                    <p className="text-sm text-gray-500 mb-1">有効企業数</p>
                                    <p className="text-3xl font-bold text-green-600">{snapshot.company.active}</p>
                                </div>
                                <div className="bg-white p-6 rounded-lg shadow">
                                    <p className="text-sm text-gray-500 mb-1">停止企業数</p>
                                    <p className="text-3xl font-bold text-red-600">{snapshot.company.suspended}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {['free', 'lite', 'standard', 'business'].map(plan => {
                                    const counts = snapshot.company.planCounts[plan] || { active: 0, registered: 0 };
                                    const planLabels = { free: 'フリー', lite: 'ライト', standard: 'スタンダード', business: 'ビジネス' };
                                    return (
                                        <div key={plan} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                            <p className="text-sm font-bold text-gray-700 mb-2">{planLabels[plan]}プラン</p>
                                            <p className="text-xl font-bold text-indigo-600">
                                                {counts.active} <span className="text-sm text-gray-400 font-normal">/ {counts.registered}</span>
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* 請求管理（アドミンのみ表示） */}
                    {isAdminRole && (
                        <section className="mb-10">
                            <h3 className="text-xl font-bold mb-4 border-l-4 border-indigo-600 pl-2">請求管理 (BillWave利用料)</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-white p-6 rounded-lg shadow border-t-4 border-red-500">
                                    <h4 className="font-bold text-gray-700 mb-4">未払い状況</h4>
                                    <div className="flex justify-between items-end">
                                        <div>
                                            <p className="text-sm text-gray-500">未払い企業数</p>
                                            <p className="text-2xl font-bold">{snapshot.billing.unpaid_count} 社</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-gray-500">未払い金額</p>
                                            <p className="text-2xl font-bold text-red-600">¥{(snapshot.billing.unpaid_amount || 0).toLocaleString()}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white p-6 rounded-lg shadow border-t-4 border-indigo-500">
                                    <h4 className="font-bold text-gray-700 mb-4">今月売上概算</h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">月額利用料</span>
                                            <span className="font-bold">¥{(snapshot.billing.monthly_base || 0).toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">従量課金</span>
                                            <span className="font-bold">¥{(snapshot.billing.monthly_overage || 0).toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between border-t pt-2 text-lg">
                                            <span className="font-bold">合計</span>
                                            <span className="font-bold text-indigo-600">¥{(snapshot.billing.monthly_total || 0).toLocaleString()}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* 利用状況（アドミンのみ表示） */}
                    {isAdminRole && (
                        <section className="mb-10">
                            <h3 className="text-xl font-bold mb-4 border-l-4 border-indigo-600 pl-2">利用状況 (CSV処理件数)</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[
                                    { label: '請求書発行 (invoice)', data: snapshot.usage.invoice },
                                    { label: '顧客登録 (import)', data: snapshot.usage.customer_import },
                                    { label: '顧客削除 (delete)', data: snapshot.usage.customer_delete }
                                ].map((item, idx) => (
                                    <div key={idx} className="bg-white p-5 rounded-lg shadow">
                                        <p className="text-sm font-bold text-gray-700 mb-3">{item.label}</p>
                                        <div className="flex justify-between">
                                            <div>
                                                <p className="text-xs text-gray-400">昨日</p>
                                                <p className="text-lg font-bold">{item.data.yesterday}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-gray-400">今月</p>
                                                <p className="text-lg font-bold text-indigo-600">{item.data.month}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}

            {/* エラー監視 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
                <section>
                    <h3 className="text-xl font-bold mb-4 border-l-4 border-red-600 pl-2">CSVアップロードエラー (直近10件)</h3>
                    <div className="bg-white shadow rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">日時</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">企業名</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">種別</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">エラー</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 text-sm">
                                {csvErrors.map(job => (
                                    <tr key={job.id}>
                                        <td className="px-4 py-2 whitespace-nowrap text-gray-500">{new Date(job.created_at).toLocaleString('ja-JP')}</td>
                                        <td className="px-4 py-2 font-medium">{job.companyName}</td>
                                        <td className="px-4 py-2">{getJobTypeName(job.type || job.job_type)}</td>
                                        <td className="px-4 py-2 text-red-600">{job.error_code || 'Error'}</td>
                                    </tr>
                                ))}
                                {csvErrors.length === 0 && <tr><td colSpan="4" className="px-4 py-4 text-center text-gray-500">エラーはありません。</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-2 text-right">
                        <Link to="/admin/csv-history" className="text-sm text-indigo-600 hover:underline">CSV履歴一覧へ</Link>
                    </div>
                </section>

                <section>
                    <h3 className="text-xl font-bold mb-4 border-l-4 border-red-600 pl-2">Stripe連携エラー (直近10件)</h3>
                    <div className="bg-white shadow rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">日時</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">企業名</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">エラー</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 text-sm">
                                {stripeErrors.map(log => (
                                    <tr key={log.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => window.location.href=`/admin/stripe-logs?billwaveCustomerId=${log.billwaveCustomerId}`}>
                                        <td className="px-4 py-2 whitespace-nowrap text-gray-500">{new Date(log.processed_at).toLocaleString('ja-JP')}</td>
                                        <td className="px-4 py-2 font-medium">{log.companyName}</td>
                                        <td className="px-4 py-2 text-red-600">{log.error_code || log.result}</td>
                                    </tr>
                                ))}
                                {stripeErrors.length === 0 && <tr><td colSpan="3" className="px-4 py-4 text-center text-gray-500">エラーはありません。</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-2 text-right">
                        <Link to="/admin/stripe-logs" className="text-sm text-indigo-600 hover:underline">連携ログ一覧へ</Link>
                    </div>
                </section>
            </div>

            {/* 最近の操作（アドミンのみ表示） */}
            {isAdminRole && (
                <section className="mb-10">
                    <h3 className="text-xl font-bold mb-4 border-l-4 border-indigo-600 pl-2">最近の操作 (直近20件)</h3>
                    <div className="bg-white shadow rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作日時</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">管理者</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作内容</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">対象</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {adminOperations.map(log => (
                                    <tr key={log.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => window.location.href=`/admin/operation-logs/${log.id}`}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(log.operated_at).toLocaleString('ja-JP')}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{log.admin_email}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getOperationTypeName(log.operation_type)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getTargetTypeName(log.target_type)}: {log.target_name || log.target_id}</td>
                                    </tr>
                                ))}
                                {adminOperations.length === 0 && <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">操作履歴はありません。</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-2 text-right">
                        <Link to="/admin/operation-logs" className="text-sm text-indigo-600 hover:underline">操作履歴一覧へ</Link>
                    </div>
                </section>
            )}

            {/* 管理メニューへのリンク */}
            <section>
                <h3 className="text-xl font-bold mb-4">管理メニュー</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Link to="/admin/companies" className="bg-white p-4 rounded-lg shadow text-center hover:bg-gray-50 border border-gray-100">
                        <p className="font-bold text-indigo-600">企業管理</p>
                    </Link>
                    {isAdminRole && (
                        <Link to="/admin/billing" className="bg-white p-4 rounded-lg shadow text-center hover:bg-gray-50 border border-gray-100">
                            <p className="font-bold text-indigo-600">請求管理</p>
                        </Link>
                    )}
                    <Link to="/admin/csv-history" className="bg-white p-4 rounded-lg shadow text-center hover:bg-gray-50 border border-gray-100">
                        <p className="font-bold text-indigo-600">CSV履歴</p>
                    </Link>
                    <Link to="/admin/stripe-logs" className="bg-white p-4 rounded-lg shadow text-center hover:bg-gray-50 border border-gray-100">
                        <p className="font-bold text-indigo-600">Stripeログ</p>
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default AdminDashboard;
